import { HTMLHint } from 'htmlhint';

import {
  LIQUID_BRACES_UNCLOSED_REGEXP,
  LIQUID_TAGS,
  LIQUID_TAGS_BROKEN_REGEXP,
  SIGNATURE_BUILTIN_PARAMS,
  TRUSTED_DOMAINS
} from '@client/enums';

const LIQUID_BRACES_REGEXP = /{{-?\s*([^}\s]+)\s*-?}}/g;
const LIQUID_ASSIGN_REGEXP = /{%\s*assign\s+([a-zA-Z_][\w]*)\s*=/g;

const LOAD_PLUGIN_REGEXP = /{%\s*load_plugin\s+["']([a-zA-Z_][\w]*)["']\s*%}/g;

const DOLLAR_IF_REGEXP = /\{if_([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
const LIQUID_IF_REGEXP = /\{%.*?if\s+([a-zA-Z_][a-zA-Z0-9_]*).*?%\}/g;

const DOLLAR_TAGS_REGEXP = /\$([a-zA-Z_][a-zA-Z0-9_]*)\s*;/g;
const LIQUID_TAGS_REGEXP = /(\{\{-?.*?\}\}|\{\%.*?-?\%\})/g;

const EXTRACT_PARAMS_REGEXP =
  /{{-?\s*([^{}]+)\s*-?}}|\$([a-zA-Z_]\w*);|\{.*?if_([a-zA-Z_][a-zA-Z0-9_]*)\}|{% if\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+%}/g;
const PARAMS_REGEXP = /{{\s*([^{}]+)\s*}}|\$([a-zA-Z_]\w*);/g;
const INTERNAL_PARAMS_REGEXP = /^(sg|sdomain|rdomain|suer|ruser|rinternal|sinternal)/;
const SPECIAL_PARAMS_REGEXP = /\[\[\s*(.*?)\s*\]\]|\$([a-zA-Z_]\w*);|{{\s*([^{}]+)\s*}}/g;

const SMART_DOMAINS_REGEXP = /^smart\.[^.]+\.[a-z]+$/i;

const SANITIZE_LIQUID_TAGS_REGEXP = /{{-?\s*(.*?)\s*-?}}/;
const SANITIZE_TEXT_PARAMS_REGEXP = /\$([a-zA-Z_]\w*);/;

export const extractTrustedPrefixes = (htmlString) => {
  const prefixes = new Set();
  let match;

  while ((match = LOAD_PLUGIN_REGEXP.exec(htmlString)) !== null) {
    prefixes.add(match[1]);
  }

  return prefixes;
};

export const extractAssignedVariables = (htmlString) => {
  const variables = new Set();
  let match;

  while ((match = LIQUID_ASSIGN_REGEXP.exec(htmlString)) !== null) {
    variables.add(match[1]);
  }

  return variables;
};

export const fixImagesInline = (html) => {
  if (!html) return html;

  return html.replace(/<img\b[^>]*>/gi, (tag) =>
    tag.replace(/(\bsrc\s*=\s*)(["'])(.*?)\2/i, (match, prefix, quote, src) => {
      if (!src) return match;

      // Replace legacy inline=true with inline=1
      if (src.includes('inline=true')) {
        return `${prefix}${quote}${src.replace(/inline=true/g, 'inline=1')}${quote}`;
      }

      if (src.includes('inline=1')) return match;

      const separator = src.includes('?') ? '&' : '?';
      return `${prefix}${quote}${src}${separator}inline=1${quote}`;
    })
  );
};

const extractImagesByPredicate = (doc, predicate) => {
  const images = [];
  doc.querySelectorAll('img').forEach((img, index) => {
    if (predicate(img)) {
      const outerHTML = img.outerHTML;
      images.push(`Image #${index + 1}: ${outerHTML.length > 80 ? `${outerHTML.substring(0, 80)}...` : outerHTML}`);
    }
  });
  return images;
};

export const extractImagesWithoutInline = (doc) =>
  extractImagesByPredicate(doc, (img) => {
    const src = img.getAttribute('src');
    // Also flag inline=true: "inline=true" contains "inline=1" as a substring,
    // so the explicit check is needed to catch it as a problem.
    return src && (src.includes('inline=true') || !src.includes('inline=1'));
  });

export const extractImagesWithoutSrc = (doc) => extractImagesByPredicate(doc, (img) => !img.hasAttribute('src'));

export const extractNonSecureUrls = (doc) => {
  const urls = new Set();

  const checkAndAddUrl = (url) => {
    if (url && url.startsWith('http:') && !url.startsWith('https://') && isValidUrl(url)) {
      urls.add(url);
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const processElements = (selector, attribute) => {
    const elements = doc.querySelectorAll(selector);

    elements.forEach((element) => {
      const rawUrl = element.getAttribute(attribute) || '';
      const cleanUrls = rawUrl
        .split(LIQUID_TAGS_REGEXP)
        .map((part) => part.trim())
        .filter((part) => part.length > 0 && isValidUrl(part));

      cleanUrls.forEach(checkAndAddUrl);
    });
  };

  processElements('a', 'href');
  processElements('img', 'src');

  return Array.from(urls).sort();
};

export const extractIncorrectWhitelabeledDomains = (doc, whitelistedDomain) => {
  const domains = new Set();

  const checkAndAddIncorrectDomain = (url) => {
    if (isValidUrl(url)) {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      const whitelistedPattern = new RegExp(`^${whitelistedDomain.replace('.', '\\.')}$`, 'i');

      if (SMART_DOMAINS_REGEXP.test(domain) && !whitelistedPattern.test(domain)) {
        domains.add(url);
      }
    }
  };

  const isValidUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch (e) {
      return false;
    }
  };

  const processElements = (selector, attribute) => {
    const elements = doc.querySelectorAll(selector);

    elements.forEach((element) => {
      const rawUrl = element.getAttribute(attribute) || '';
      const cleanUrls = rawUrl
        .split(LIQUID_TAGS_REGEXP)
        .map((part) => part.trim())
        .filter((part) => part.length > 0 && isValidUrl(part));

      cleanUrls.forEach(checkAndAddIncorrectDomain);
    });
  };

  processElements('a', 'href');
  processElements('img', 'src');

  return Array.from(domains).sort();
};

export const extractActiveParams = (htmlString) => {
  const active = new Set();

  const findParams = (pattern, text) => {
    let match;

    while ((match = pattern.exec(text)) !== null) {
      const param = match[1];
      active.add(param);
    }
  };

  findParams(LIQUID_BRACES_REGEXP, htmlString);
  findParams(DOLLAR_TAGS_REGEXP, htmlString);
  findParams(LIQUID_IF_REGEXP, htmlString);
  findParams(DOLLAR_IF_REGEXP, htmlString);

  return Array.from(active).sort();
};

export const extractMissingParams = (htmlString, paramsArray) => {
  const params = paramsArray.map((param) => param.name);
  const missing = new Set();
  const trustedPrefixes = extractTrustedPrefixes(htmlString);
  const assignedVariables = extractAssignedVariables(htmlString);

  const isTrustedReference = (param) => {
    return (
      [...trustedPrefixes].some((prefix) => param === prefix || param.startsWith(`${prefix}.`)) ||
      assignedVariables.has(param)
    );
  };

  const findParams = (pattern, text) => {
    let match;

    while ((match = pattern.exec(text)) !== null) {
      const param = match[1];

      if (
        param &&
        !params.includes(param) &&
        !SIGNATURE_BUILTIN_PARAMS.includes(param) &&
        !isTrustedReference(param) &&
        !INTERNAL_PARAMS_REGEXP.test(param)
      ) {
        missing.add(param);
      }
    }
  };

  findParams(LIQUID_BRACES_REGEXP, htmlString);
  findParams(DOLLAR_TAGS_REGEXP, htmlString);
  findParams(LIQUID_IF_REGEXP, htmlString);

  return Array.from(missing).sort();
};

export const extractUnusedParams = (htmlString, paramsArray) => {
  const params = paramsArray.map((param) => param.name);
  const unused = new Set();

  const findParams = (paramName) => {
    const LIQUID_PARAM_REGEXP = new RegExp(`{{-?\\s*${paramName}\\s*-?}}`, 'g');
    const DOLLAR_PARAM_REGEXP = new RegExp(`\\$${paramName}\\b`, 'g');
    const LIQUID_IF_PARAM_REGEXP = new RegExp(
      `{%.*?if\\s+(${paramName})\\s*(==|!=|>|<|>=|<=)?\\s*(".*?"|'.*?'|[a-zA-Z_][a-zA-Z0-9_]*)?.*?%}`,
      'g'
    );
    const DOLLAR_IF_PARAM_REGEXP = new RegExp(
      `\\{if_${paramName}(==|!=|>|<|>=|<=)?(".*?"|'.*?'|[a-zA-Z_][a-zA-Z0-9_]*)?\\}`,
      'g'
    );

    if (
      !LIQUID_PARAM_REGEXP.test(htmlString) &&
      !DOLLAR_PARAM_REGEXP.test(htmlString) &&
      !LIQUID_IF_PARAM_REGEXP.test(htmlString) &&
      !DOLLAR_IF_PARAM_REGEXP.test(htmlString) &&
      !INTERNAL_PARAMS_REGEXP.test(paramName)
    ) {
      unused.add(paramName);
    }
  };

  params.forEach(findParams);

  return Array.from(unused).sort();
};

export const extractInternalParams = (template) => {
  const extracted = template?.match(EXTRACT_PARAMS_REGEXP) || [];
  const internal = new Set();

  let match;

  while ((match = PARAMS_REGEXP.exec(extracted.join('\n'))) !== null) {
    const param = match[1] || match[2] || match[3] || match[4];

    if (param) {
      // Strip liquid filters (everything after the first pipe character)
      const cleanParam = param.split('|')[0].trim();

      if (cleanParam && INTERNAL_PARAMS_REGEXP.test(cleanParam)) {
        internal.add(cleanParam);
      }
    }
  }

  return Array.from(internal).sort();
};

export const extractSpecialParams = (template) => {
  const extracted = (template?.match(SPECIAL_PARAMS_REGEXP) || []).join('\n');
  const config = {};

  let match;

  while ((match = SPECIAL_PARAMS_REGEXP.exec(extracted)) !== null) {
    if (match[1]) {
      let [type, value] = match[1].split(':');

      if (!value) {
        value = type;
        type = 'adtags';
      }

      if (!config[type]) {
        config[type] = [];
      }

      if (value) {
        config[type].push(
          value.replace(SANITIZE_TEXT_PARAMS_REGEXP, '$1').replace(SANITIZE_LIQUID_TAGS_REGEXP, '$1').trim()
        );
      }
    }
  }

  Object.keys(config).forEach((key) => {
    config[key] = config[key].sort((a, b) => a.localeCompare(b));
  });

  return config;
};

export const extractThirdPartyUrls = (doc, additionalTrustedDomains = [], currentDomain = '') => {
  const thirdPartyUrls = new Set();
  const domains = [...TRUSTED_DOMAINS, ...additionalTrustedDomains];

  const checkAndAddThirdPartyUrl = (url) => {
    if (isValidUrl(url)) {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;

      if (!isTrustedDomain(domain, domains) && !domain.startsWith('smart.') && !currentDomain.indexOf(domain) === -1) {
        thirdPartyUrls.add(url);
      }
    }
  };

  const isTrustedDomain = (domain, trustedDomains) => {
    return trustedDomains.some((trustedDomain) => {
      const regex = new RegExp(`(^|\\.)${trustedDomain.replace('.', '\\.')}$`, 'i');
      return regex.test(domain);
    });
  };

  const isValidUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch (e) {
      return false;
    }
  };

  const processElements = (selector, attribute) => {
    const elements = doc.querySelectorAll(selector);

    elements.forEach((element) => {
      const rawUrl = element.getAttribute(attribute) || '';
      const cleanUrls = rawUrl
        .split(LIQUID_TAGS_REGEXP)
        .map((part) => part.trim())
        .filter((part) => part.length > 0 && isValidUrl(part));

      cleanUrls.forEach(checkAndAddThirdPartyUrl);
    });
  };

  processElements('a', 'href');
  processElements('img', 'src');

  return Array.from(thirdPartyUrls).sort();
};

export const lintHTMLContent = (content = '') => {
  const results = HTMLHint.verify(content, {
    'tagname-lowercase': true,
    'attr-lowercase': true,
    'attr-value-double-quotes': true,
    'doctype-first': false,
    'tag-pair': true,
    'spec-char-escape': false,
    'id-unique': true,
    'src-not-empty': true,
    'attr-no-duplication': true
  });

  return results;
};

export const lintLiquidContent = (content) => {
  const diagnostics = [];

  let regex = LIQUID_BRACES_UNCLOSED_REGEXP;
  let match;

  while ((match = regex.exec(content)) !== null) {
    diagnostics.push({
      from: match.index,
      to: match.index + match[0].length,
      line: 'n/a',
      severity: 'error',
      message: 'Unclosed Liquid tag or object.'
    });
  }

  regex = LIQUID_TAGS_BROKEN_REGEXP;

  while ((match = regex.exec(content)) !== null) {
    if (!LIQUID_TAGS.includes(match[1])) {
      diagnostics.push({
        from: match.index,
        to: match.index + match[0].length,
        line: 'n/a',
        severity: 'error',
        message: `Unknown Liquid tag: ${match[1]}`
      });
    }
  }

  return diagnostics;
};

export const detectSignatureIssues = (
  signature,
  smartdomain,
  additionalTrustedDomains = [],
  template = 'htmltemplate',
  params = 'params',
  options = {}
) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(signature[template], 'text/html');
  const nonSecureUrls = extractNonSecureUrls(doc).map((data) => ({ type: 'non_secure_urls', data }));

  const incorrectWhiteLabeledDomain = extractIncorrectWhitelabeledDomains(doc, smartdomain).map((data) => ({
    type: 'smart_domain_mismatch',
    data
  }));

  const unusedParams = extractUnusedParams(signature[template], signature[params]).map((data) => ({
    type: 'unused_params',
    data
  }));

  const thirdPartyUrls = extractThirdPartyUrls(doc, additionalTrustedDomains, signature?.currentDomain).map((data) => ({
    type: 'third_party_urls',
    data
  }));

  const missingParams = extractMissingParams(signature[template], signature[params]).map((data) => ({
    type: 'missing_params',
    data
  }));

  const imagesWithoutSrc = extractImagesWithoutSrc(doc).map((data) => ({
    type: 'images_without_src',
    data
  }));

  const imagesWithoutInline = options.isOutlookVisualPreview
    ? extractImagesWithoutInline(doc).map((data) => ({ type: 'images_without_inline', data }))
    : [];

  const htmlLintErrors = lintHTMLContent(signature[template]).map(({ col, line, message }) => ({
    type: 'html_lint_errors',
    data: `${message.replace('.', '')} at line ${line} column ${col}.`
  }));

  const liquidLintErrors = lintLiquidContent(signature[template]).map(({ from, to, message }) => ({
    type: 'html_lint_errors',
    data: `${message} at position ${from}-${to}.`
  }));

  return [
    ...missingParams,
    ...imagesWithoutSrc,
    ...imagesWithoutInline,
    ...htmlLintErrors,
    ...liquidLintErrors,
    ...nonSecureUrls,
    ...incorrectWhiteLabeledDomain,
    ...thirdPartyUrls,
    ...unusedParams
  ];
};

export const getSignatureWarnings = (
  signature,
  smartdomain = '',
  additionalTrustedDomains = [],
  template = 'htmltemplate',
  params = 'params',
  options = {}
) => {
  const results = detectSignatureIssues(signature, smartdomain, additionalTrustedDomains, template, params, options);

  const warnings = results.reduce((acc, { type, data }) => {
    if (acc[type]) {
      acc[type].push(data);
    } else {
      acc[type] = [data];
    }

    return acc;
  }, []);

  return Object.entries(warnings).map(([type, data]) => ({ type, data }));
};

export const getAllSignatureWarnings = (
  signature,
  smartdomain = '',
  additionalTrustedDomains = [],
  excludeTypes = [],
  options = {}
) => {
  if (!signature) return [];

  const results = detectSignatureIssues(
    signature,
    smartdomain,
    additionalTrustedDomains,
    'htmltemplate',
    'params',
    options
  );

  // Also scan the reply template when it is enabled, so reply-only issues (e.g. HTML errors)
  // surface on the summary badge instead of being hidden until the card is opened and scrolled.
  // Reply items are prefixed so the tooltip distinguishes them from primary-template items.
  const replyResults = signature.replytemplateenabled
    ? detectSignatureIssues(
        signature,
        smartdomain,
        additionalTrustedDomains,
        'replyhtmltemplate',
        'params',
        options
      ).map(({ type, data }) => ({ type, data: `Reply: ${data}` }))
    : [];

  const filteredResults = [...results, ...replyResults].filter(({ type }) => !excludeTypes.includes(type));

  const warnings = filteredResults.reduce((acc, { type, data }) => {
    if (acc[type]) {
      acc[type].push(data);
    } else {
      acc[type] = [data];
    }

    return acc;
  }, []);

  return Object.entries(warnings).map(([type, data]) => ({ type, data }));
};
